import { Component, Element, Listen, h, State, Prop } from '@stencil/core';
import { CardData } from '../_types/card-data';
import { ButtonClickData } from '../button/button';
import {
	calculateChildTagName,
	getChildComponents,
} from '../_helpers/prefixes';
import { Breakpoint, getCurrentBreakpoint } from '../_helpers/breakpoints';
import {
	moveInlineStylesToHead,
	removeInlineStylesFromHead,
} from '../_helpers/content-security-policy';

const CardCountToShow = 6;
const CardCountToShowOnXL = 8;

const CardCountIncrement = 6;
const CardCountIncrementOnXL = 8;

@Component({
	tag: 'ds-filterable-cards',
	shadow: false,
})
export class FilterableCards {
	@Element()
	el: HTMLElement;

	@State()
	selected: string;

	@State()
	innerCardsData: CardData[];

	@State()
	filterOptionsList: string[];

	@State()
	showCount: number;

	@State()
	cardElList: HTMLElement[];

	@State()
	showMoreBtnVisible: boolean;

	@Prop({ attribute: 'default-filter-option' })
	defaultFilter = 'All insights';

	@Prop({ attribute: 'cards-data' })
	cardsData: string;

	@Prop({ attribute: 'filter-column-class' })
	filterColumnClass: string;

	@Prop({ attribute: 'dropdown-label' })
	dropdownLabel: string;

	@Prop({ attribute: 'show-filter' })
	showFilter = true;

	private cardCount = this.getCardCountForBreakpoint();
	private cardIncrement = this.getIncrementCountForBreakpoint();

	@Listen('resize', { target: 'window' })
	async windowResizeHandler(): Promise<void> {
		this.cardCount = this.getCardCountForBreakpoint();
		this.cardIncrement = this.getIncrementCountForBreakpoint();
	}

	getCardCountForBreakpoint() {
		if (Breakpoint.XL === getCurrentBreakpoint()) return CardCountToShowOnXL;
		else return CardCountToShow;
	}

	getIncrementCountForBreakpoint() {
		if (Breakpoint.XL === getCurrentBreakpoint()) return CardCountIncrementOnXL;
		else return CardCountIncrement;
	}

	@Listen('optionSelect')
	optionSelectHandler(event): void {
		this.showCount = this.cardCount;
		if (event.detail === this.defaultFilter) {
			this.selected = event.detail;
			this.hideAllCards();
			this.showAllCards();
			return;
		}

		if (!this.selected || this.selected !== event.detail) {
			this.selected = event.detail;
			this.hideAllCards();
			this.showCardsOfSelectedCategory();
		}
	}

	componentWillLoad() {
		this.showCount = this.cardCount;
		this.handleCardsDataChange(this.cardsData);
		this.cardElList = getChildComponents<HTMLElement>(
			this.el,
			'.ds-dal-article'
		);
		this.selected = this.defaultFilter;
		this.hideAllCards();
		this.showAllCards();
		setTimeout(() => {
			moveInlineStylesToHead(this.el.id);
		}, 700);
	}

	componentDidLoad() {
		setTimeout(() => {
			moveInlineStylesToHead(this.el.id);
		}, 700);
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
	}

	showAllCards = (): void => {
		this.cardElList.forEach((card: HTMLElement, index: number) => {
			if (card.classList.contains('ds-invisible') && index < this.showCount) {
				card.classList.remove('ds-invisible');
			}
		});
		this.showMoreBtnVisible = this.cardElList.length > this.showCount;
	};

	hideAllCards = (): void => {
		this.cardElList.forEach((card: HTMLElement) => {
			if (!card.classList.contains('ds-invisible')) {
				card.classList.add('ds-invisible');
			}
		});
	};

	replaceSpaceWithUnderscore = (text: string) => text.replace(/ /g, '_');

	showCardsOfSelectedCategory = (): void => {
		const selectedNodes = this.el.querySelectorAll(
			`.ds-category-${this.replaceSpaceWithUnderscore(this.selected)}`
		);

		selectedNodes.forEach((card: Element, index: number) => {
			if (index < this.showCount) {
				card.classList.remove('ds-invisible');
			}
		});

		this.showMoreBtnVisible = selectedNodes.length > this.showCount;
	};

	handleCardsDataChange = (newData): void => {
		this.innerCardsData = JSON.parse(newData);
		this.filterOptionsList = this.innerCardsData
			.reduce((acc, { category }) => {
				if (acc && acc.indexOf(category) === -1) {
					acc.push(category);
				}
				return acc;
			}, [])
			.sort((a, b) => a.localeCompare(b));
		this.filterOptionsList.unshift(this.defaultFilter);
	};

	@Listen('buttonClick')
	buttonClickHandler(event: CustomEvent<ButtonClickData>) {
		if (event.detail.component.classList.contains('ds-load-more-button')) {
			const cards = getChildComponents<HTMLDsPageCardElement>(
				this.el,
				'ds-page-card'
			);
			cards.forEach((card) => {
				// Prevents card flickering
				if (card?.revealAnimation === 'delayed')
					card.setAttribute('reveal-animation', 'on');
			});
			this.showCount += this.cardIncrement;

			this.selected === this.defaultFilter
				? this.showAllCards()
				: this.showCardsOfSelectedCategory();
		}
	}

	render() {
		const Dropdown = calculateChildTagName(this.el, 'ds-dropdown');
		return (
			<div>
				{this.showFilter ? (
					<div class={'row'}>
						<div class={this.filterColumnClass}>
							<Dropdown
								option-list={JSON.stringify(this.filterOptionsList)}
								dropdown-label={this.dropdownLabel}
								pre-filled-option-index="1"
								appearance="subdued"
							></Dropdown>
						</div>
					</div>
				) : (
					''
				)}

				<slot name="cards" />

				<div class={'row'}>
					<div
						class={`ds-load-more-wrapper ${
							this.showMoreBtnVisible ? '' : 'ds-more-btn-invisible'
						}`}
					>
						<slot name="load-more-button" />
					</div>
				</div>
			</div>
		);
	}
}
